"use client";

import { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CarDetails } from "@/lib/actions/car-details";
import { submitInquiry } from "@/lib/actions/car-details";
import { 
  Building2, 
  Mail, 
  MapPin, 
  Phone, 
  User, 
  MessageSquare,
  Globe,
  Clock,
  Calendar,
  Star,
  Shield,
  InfoIcon,
  CarIcon,
  Info
} from "lucide-react";
import { FaWhatsapp, FaPhone, FaEnvelope, FaComments } from "react-icons/fa";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

interface ContactCardProps {
  organization: CarDetails["organization"];
  car: CarDetails;
}

// Format phone number for WhatsApp
const formatPhoneForWhatsApp = (phoneNumber: string): string => {
  // Remove any non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // If it doesn't start with a country code, add Kenya's country code (+254)
  if (!cleaned.startsWith('254') && !cleaned.startsWith('1')) {
    // If it starts with 0, replace it with 254
    if (cleaned.startsWith('0')) {
      return '254' + cleaned.substring(1);
    }
    // Otherwise, just add 254 prefix
    return '254' + cleaned;
  }
  
  return cleaned;
};

// Add interface for the organization data to fix the type error
interface OrganizationFallback {
  id: string;
  name: string;
  logo: string | null;
  phone: string | null;
  phoneNumber?: string | null; // Make it optional with ? to match potential structure
  email: string | null;
  address: string | null;
  metadata?: {
    phone?: string;
    address?: string;
    description?: string;
  };
}

export function ContactCard({ organization, car }: ContactCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: `I'm interested in the ${car.year} ${car.make?.name || ''} ${car.model?.name || ''}. Please contact me with more information.`
  });

  // Default organization data if it's undefined
  const orgData: OrganizationFallback = organization || {
    id: "",
    name: "Seller",
    logo: null,
    phone: null,
    email: null,
    address: null
  };

  // Ensure phone number is available - use a fallback for demo/testing if needed
  const phoneNumber = organization?.metadata?.phone || // Access from metadata JSON
                     organization?.phone || 
                     (organization as { phoneNumber?: string })?.phoneNumber || 
                     "+254712345678"; // Fallback for testing/demo
  
  // Always generate WhatsApp link even with fallback phone
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const whatsappLink = `https://wa.me/${formatPhoneForWhatsApp(phoneNumber)}?text=${encodeURIComponent(`Hello, I'm interested in the ${car.year} ${car.make?.name || ''} ${car.model?.name || ''} (ID: ${car.id}) listed on KashiAndKarz. View it here: ${currentUrl}`)}`;

  // Get organization address from metadata
  const orgAddress = organization?.metadata?.address || organization?.address || null;

  // Get organization description from metadata
  const orgDescription = organization?.metadata?.description || null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await submitInquiry({
        carId: car.id,
        organizationId: orgData.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });

      if (result.success) {
        toast.success("Message sent!", {
          description: "We've forwarded your inquiry to the seller."
        });
        setIsContactDialogOpen(false);
      } else {
        throw new Error(result.error || "Failed to send message");
      }
    } catch (err) {
      console.error("Error submitting inquiry:", err);
      toast.error("Error", {
        description: "There was a problem sending your message. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="relative rounded-xl overflow-hidden glossy-card"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
      
      <div className="p-6 relative z-10 space-y-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span className="text-sm">Seller Information</span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="relative h-16 w-16 rounded-full bg-muted/30 flex items-center justify-center shrink-0 border border-primary/10">
            {orgData.logo ? (
              <Image 
                src={orgData.logo} 
                alt={orgData.name} 
                fill 
                className="rounded-full object-cover"
              />
            ) : (
              <Building2 className="h-8 w-8 text-primary/50" />
            )}
            <div className="absolute inset-0 rounded-full neon-glow"></div>
          </div>
          
          <div>
            <h3 className="font-semibold text-lg">{orgData.name}</h3>
            <p className="text-sm text-muted-foreground">Verified Seller</p>
          </div>
        </div>
        
        <div className="space-y-3 pt-2">
          {phoneNumber && (
            <div className="flex items-center gap-3 text-sm">
              <div className="relative h-8 w-8 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                <Phone className="h-4 w-4 text-primary" />
                <div className="absolute inset-0 rounded-full neon-glow"></div>
              </div>
              <a href={`tel:${phoneNumber}`} className="hover:text-primary transition-colors">
                {phoneNumber}
              </a>
            </div>
          )}
          
          {whatsappLink && (
            <div className="flex items-center gap-3 text-sm">
              <div className="relative h-8 w-8 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                <FaWhatsapp className="h-4 w-4 text-primary" />
                <div className="absolute inset-0 rounded-full neon-glow"></div>
              </div>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                Chat on WhatsApp
              </a>
            </div>
          )}
          
          {orgData.email && (
            <div className="flex items-center gap-3 text-sm">
              <div className="relative h-8 w-8 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                <Mail className="h-4 w-4 text-primary" />
                <div className="absolute inset-0 rounded-full neon-glow"></div>
              </div>
              <a href={`mailto:${orgData.email}`} className="hover:text-primary transition-colors">
                {orgData.email}
              </a>
            </div>
          )}
          
          {orgAddress && (
            <div className="flex items-center gap-3 text-sm">
              <div className="relative h-8 w-8 rounded-full bg-primary/5 flex items-center justify-center shrink-0">
                <MapPin className="h-4 w-4 text-primary" />
                <div className="absolute inset-0 rounded-full neon-glow"></div>
              </div>
              <address className="not-italic">{orgAddress}</address>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          {/* Organization Info Dialog */}
          <Dialog open={isOrgDialogOpen} onOpenChange={setIsOrgDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto rounded-full hover-glow gap-2 border-primary/20"
              >
                <InfoIcon className="h-4 w-4" />
                <span>About Seller</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl border-primary/20 neon-glow bg-background/95 backdrop-blur-sm">
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="relative h-20 w-20 rounded-full bg-muted/30 flex items-center justify-center shrink-0 border border-primary/20 neon-glow">
                    {orgData.logo ? (
                      <Image 
                        src={orgData.logo} 
                        alt={orgData.name} 
                        fill 
                        className="rounded-full object-cover"
                      />
                    ) : (
                      <Building2 className="h-10 w-10 text-primary/50" />
                    )}
                  </div>
                  <div>
                    <DialogTitle className="text-2xl">{orgData.name}</DialogTitle>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                        Verified
                      </Badge>
                      <Badge variant="outline" className="bg-muted/20 text-foreground border-muted/30">
                        Premium Dealer
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <InfoIcon className="h-4 w-4 text-primary" />
                    Contact Information
                  </h3>
                  
                  <div className="space-y-3">
                    {phoneNumber && (
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Phone className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <a href={`tel:${phoneNumber}`} className="hover:text-primary transition-colors">
                            {phoneNumber}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {whatsappLink && (
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <FaWhatsapp className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">WhatsApp</p>
                          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                            Chat on WhatsApp
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {orgData.email && (
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Mail className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Email</p>
                          <a href={`mailto:${orgData.email}`} className="hover:text-primary transition-colors">
                            {orgData.email}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {orgAddress && (
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                          <MapPin className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Address</p>
                          <address className="not-italic">{orgAddress}</address>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    Seller Ratings
                  </h3>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Customer Satisfaction</span>
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            className="h-4 w-4 text-yellow-500 fill-yellow-500" 
                          />
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Response Time</span>
                      <div className="flex items-center">
                        {[1, 2, 3, 4].map((star) => (
                          <Star 
                            key={star} 
                            className="h-4 w-4 text-yellow-500 fill-yellow-500" 
                          />
                        ))}
                        <Star className="h-4 w-4 text-yellow-500" />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Vehicle Quality</span>
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            className="h-4 w-4 text-yellow-500 fill-yellow-500" 
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <Separator className="my-6" />
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  About {orgData.name}
                </h3>
                
                <p className="text-muted-foreground">
                  {orgData.name} is a trusted vehicle dealer specializing in premium automotive solutions. 
                  With years of experience in the industry, we provide high-quality vehicles and exceptional 
                  customer service to ensure 100% satisfaction.
                </p>
                
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span className="text-sm">Established: 2010</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <CarIcon className="h-4 w-4 text-primary" />
                    <span className="text-sm">Vehicles: 120+</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    <span className="text-sm">Website: kashiandkarz.com</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="text-sm">Hours: 9am - 6pm</span>
                  </div>
                </div>

                {/* About section with description */}
                {orgDescription && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium flex items-center gap-2">
                      <Info className="h-4 w-4 text-primary" />
                      About Us
                    </h3>
                    <p className="text-sm text-muted-foreground">{orgDescription}</p>
                  </div>
                )}
              </div>
              
              <DialogFooter className="mt-6">
                {whatsappLink && (
                  <Button 
                    variant="outline"
                    className="gap-2 w-full sm:w-auto mr-2 border-primary/20 hover:bg-primary/10"
                    onClick={() => window.open(whatsappLink, '_blank')}
                  >
                    <FaWhatsapp className="h-4 w-4 text-primary" />
                    WhatsApp
                  </Button>
                )}
                <Button 
                  className="gap-2 w-full sm:w-auto"
                  onClick={() => {
                    setIsOrgDialogOpen(false);
                    setIsContactDialogOpen(true);
                  }}
                >
                  <MessageSquare className="h-4 w-4" />
                  Contact {orgData.name}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Direct WhatsApp Button */}
          {whatsappLink && (
            <Button 
              variant="outline"
              className="w-full sm:w-auto rounded-full hover-glow gap-2 border-primary/20 hover:bg-primary/10"
              onClick={() => window.open(whatsappLink, '_blank')}
            >
              <FaWhatsapp className="h-4 w-4 text-primary" />
              <span>WhatsApp</span>
            </Button>
          )}
          
          {/* Contact Dialog */}
          <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full rounded-full hover-glow gap-2">
                <MessageSquare className="h-4 w-4" />
                Message Seller
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md border-primary/20 neon-glow bg-background/95 backdrop-blur-sm max-h-[90vh] overflow-y-auto">
              <div className="absolute inset-0 bg-grid-pattern opacity-5 rounded-lg"></div>
              <DialogHeader className="relative z-10">
                <DialogTitle className="text-center text-xl bg-gradient-to-r from-primary to-indigo-500 bg-clip-text text-transparent font-semibold">
                  Contact {orgData.name}
                </DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  About this {car.year} {car.make?.name} {car.model?.name}
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="message" className="relative z-10">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="message" className="flex items-center gap-1">
                    <FaComments className="h-3.5 w-3.5" />
                    <span>Message</span>
                  </TabsTrigger>
                  <TabsTrigger value="whatsapp" className="flex items-center gap-1">
                    <FaWhatsapp className="h-3.5 w-3.5" />
                    <span>WhatsApp</span>
                  </TabsTrigger>
                  <TabsTrigger value="call" className="flex items-center gap-1">
                    <FaPhone className="h-3.5 w-3.5" />
                    <span>Call</span>
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="message">
                  <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 border border-primary/20 rounded-md pl-3 bg-background/50 backdrop-blur-sm hover:border-primary/40 focus-within:border-primary/50 focus-within:neon-glow transition-all">
                        <User className="h-4 w-4 text-primary/70" />
                        <Input
                          name="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 border border-primary/20 rounded-md pl-3 bg-background/50 backdrop-blur-sm hover:border-primary/40 focus-within:border-primary/50 focus-within:neon-glow transition-all">
                        <FaEnvelope className="h-4 w-4 text-primary/70" />
                        <Input
                          name="email"
                          type="email"
                          placeholder="Your email address"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 border border-primary/20 rounded-md pl-3 bg-background/50 backdrop-blur-sm hover:border-primary/40 focus-within:border-primary/50 focus-within:neon-glow transition-all">
                        <FaPhone className="h-4 w-4 text-primary/70" />
                        <Input
                          name="phone"
                          placeholder="Your phone number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="border border-primary/20 rounded-md p-3 bg-background/50 backdrop-blur-sm hover:border-primary/40 focus-within:border-primary/50 focus-within:neon-glow transition-all">
                        <Textarea
                          name="message"
                          placeholder="Message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          className="border-0 resize-none min-h-[120px] focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                        />
                      </div>
                    </div>
                    
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button 
                        type="submit" 
                        className="w-full rounded-full shadow-[0_0_15px_rgba(0,225,225,0.3)] hover:shadow-[0_0_20px_rgba(0,225,225,0.5)] transition-all"
                        disabled={isLoading}
                      >
                        {isLoading ? "Sending..." : "Send Message"}
                      </Button>
                    </motion.div>
                  </form>
                </TabsContent>
                
                <TabsContent value="whatsapp" className="flex flex-col items-center justify-center py-6">
                  <div className="relative h-16 w-16 rounded-full bg-primary/5 flex items-center justify-center mb-4">
                    <FaWhatsapp className="h-8 w-8 text-primary" />
                    <div className="absolute inset-0 rounded-full neon-glow"></div>
                  </div>
                  <h3 className="text-lg font-medium mb-2">Contact via WhatsApp</h3>
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Chat directly with {orgData.name} about this vehicle
                  </p>
                  <div className="text-xl font-bold text-center mb-4">
                    {phoneNumber}
                  </div>
                  <Button 
                    className="gap-2 rounded-full mb-2"
                    onClick={() => window.open(whatsappLink, '_blank')}
                  >
                    <FaWhatsapp className="h-4 w-4" />
                    Open WhatsApp
                  </Button>
                  <div className="text-sm text-muted-foreground text-center mt-4">
                    <p>Organization: {orgData.name}</p>
                    {orgData.email && <p>Email: {orgData.email}</p>}
                    {orgAddress && <p>Address: {orgAddress}</p>}
                    <p className="mt-2 text-xs">Message includes car details and link</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="call" className="flex flex-col items-center justify-center py-6">
                  <div className="relative h-16 w-16 rounded-full bg-primary/5 flex items-center justify-center mb-4">
                    <FaPhone className="h-7 w-7 text-primary" />
                    <div className="absolute inset-0 rounded-full neon-glow"></div>
                  </div>
                  <h3 className="text-lg font-medium mb-2">Call {orgData.name}</h3>
                  <p className="text-sm text-muted-foreground text-center mb-6">
                    Call directly to inquire about this vehicle
                  </p>
                  <div className="text-xl font-bold text-center mb-4">
                    {phoneNumber}
                  </div>
                  <Button 
                    className="gap-2 rounded-full"
                    asChild
                  >
                    <a href={`tel:${phoneNumber}`}>
                      <FaPhone className="h-4 w-4" />
                      Call Now
                    </a>
                  </Button>
                  <div className="text-sm text-muted-foreground text-center mt-4">
                    <p>Organization: {orgData.name}</p>
                    {orgData.email && <p>Email: {orgData.email}</p>}
                    {orgAddress && <p>Address: {orgAddress}</p>}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </motion.div>
  );
} 